/**
 * File validation utilities for secure file uploads
 */

// Allowed file extensions for Excel files
const ALLOWED_EXTENSIONS = [".xlsx", ".xls"];

// Maximum file size (10MB)
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Allowed MIME types for Excel files
const ALLOWED_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel", // .xls
];

/**
 * Validates a file for upload
 * @param {File} file - The file to validate
 * @param {Object} options - Optional configuration
 * @param {string[]} options.allowedExtensions - Allowed file extensions
 * @param {number} options.maxSize - Maximum file size in bytes
 * @returns {{ valid: boolean, error?: string }} Validation result
 */
export const validateFile = (file, options = {}) => {
  const { allowedExtensions = ALLOWED_EXTENSIONS, maxSize = MAX_FILE_SIZE } =
    options;

  if (!file) {
    return { valid: false, error: "No file provided" };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const ext = "." + fileName.split(".").pop();
  if (!allowedExtensions.includes(ext)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: ${allowedExtensions.join(", ")}`,
    };
  }

  // Check file size
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File too large. Maximum size: ${maxSizeMB}MB`,
    };
  }

  // Check MIME type (if available)
  if (file.type && !ALLOWED_MIME_TYPES.includes(file.type)) {
    // Some browsers may not set the type correctly, so we only warn
    console.warn(`Unexpected MIME type: ${file.type}`);
  }

  return { valid: true };
};

/**
 * Validates multiple files for upload
 * @param {File[]} files - Array of files to validate
 * @param {Object} options - Optional configuration
 * @returns {{ valid: boolean, error?: string }} Validation result
 */
export const validateFiles = (files, options = {}) => {
  if (!files || files.length === 0) {
    return { valid: false, error: "No files provided" };
  }

  for (const file of files) {
    const result = validateFile(file, options);
    if (!result.valid) {
      return result;
    }
  }

  return { valid: true };
};

export { ALLOWED_EXTENSIONS, MAX_FILE_SIZE, ALLOWED_MIME_TYPES };
